import { useState } from 'react'
import { outbox } from '../lib/outbox'
import { useConnection, useOutboxItems } from '../lib/hooks'
import { ago, money, num } from '../lib/format'
import { Banner, Empty, Evidence, IconWifiOff, Panel, Pill, StatMini } from '../ui/kit'
import { FieldHeader } from './FieldShell'

export function Queue() {
  const items = useOutboxItems()
  const { online, offlineMinutes } = useConnection()
  const [flushing, setFlushing] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const pending = items.filter((i) => i.status !== 'rejected')
  const rejected = items.filter((i) => i.status === 'rejected')
  const totalValue = pending.reduce((s, i) => s + (i.amount ?? 0), 0)
  const totalWeight = pending.reduce((s, i) => s + (i.weightKg ?? 0), 0)

  async function flush() {
    setFlushing(true)
    setResult(null)
    const r = await outbox.flush()
    setFlushing(false)
    if (!r) setResult('Could not reach the server. Everything is still here.')
    else setResult(`${r.applied} sent, ${r.replayed} already had, ${r.rejected} refused.`)
  }

  return (
    <>
      <FieldHeader />
      <div className="field-greet">
        <h1>Not sent yet</h1>
        <p>Work saved on this phone that has not reached the office yet.</p>
      </div>

      <div className="field-body">
        {!online && (
          <Banner
            tone="warn"
            icon={<IconWifiOff size={16} />}
            title={`Offline${offlineMinutes > 0 ? ` — ${offlineMinutes} min` : ''}`}
          >
            Everything below is safe on this phone. It sends itself automatically the moment you are
            back on a signal.
          </Banner>
        )}

        <div className="brand-card">
          <div className="spread">
            <span className="bc-hero num">{pending.length}</span>
            <span className="micro">items queued</span>
          </div>
          <div className="bc-grid">
            <div>
              <b className="num">{money(totalValue, { compact: true })}</b>
              <i>Value</i>
            </div>
            <div>
              <b className="num">{num(totalWeight, 1)} kg</b>
              <i>Weight</i>
            </div>
            <div>
              <b className="num">{rejected.length}</b>
              <i>Refused</i>
            </div>
          </div>
        </div>

        {pending.length === 0 && rejected.length === 0 && (
          <Empty title="Everything has been sent">
            Every collection and payment on this device has reached the server.
          </Empty>
        )}

        {pending.length > 0 && (
          <Panel title="Waiting to send" meta="photos and GPS attached">
            <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {pending.map((i, idx) => (
                <div key={i.clientUuid} className="stop-row" style={{ cursor: 'default' }}>
                  <span className="stop-state pending">{idx + 1}</span>
                  <span className="stop-who">
                    <b>{i.label}</b>
                    <i>
                      {i.sublabel}
                      {i.attempts > 0 && ` · ${i.attempts} attempt${i.attempts > 1 ? 's' : ''}`}
                    </i>
                    <div style={{ marginTop: 4 }}>
                      <Evidence
                        photo={!!i.payload.photoUrl}
                        gps={i.payload.lat !== null && i.payload.lat !== undefined}
                      />
                    </div>
                  </span>
                  <span className="stop-amt">
                    <b>{i.amount ? money(i.amount, { currency: false }) : num(i.weightKg, 1)}</b>
                    <span>{i.status === 'sending' ? 'sending…' : 'queued ✓'}</span>
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        )}

        {rejected.length > 0 && (
          <Panel title="Refused by the server" meta={<Pill tone="crit">{rejected.length}</Pill>}>
            <div className="panel-body col" style={{ gap: 12 }}>
              <p className="hint">
                These were not applied. They stay here rather than disappearing quietly — a farmer's
                payment vanishing silently would be the worst possible failure.
              </p>
              {rejected.map((i) => (
                <div key={i.clientUuid} className="banner crit" style={{ padding: 'var(--sp-6)' }}>
                  <div className="grow">
                    <b>{i.label}</b>
                    <p>{i.errorDetail ?? i.errorCode ?? 'The server refused this.'}</p>
                    <button
                      className="btn quiet"
                      style={{ marginTop: 8, minHeight: 32 }}
                      onClick={() => void outbox.discard(i.clientUuid)}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        )}

        <Banner tone="info" title="Nobody gets paid twice">
          Each payment waiting here is stamped before it leaves the phone. If the same one is sent
          again after you get signal, it is blocked and the office is told.
        </Banner>

        {result && <p className="hint" style={{ textAlign: 'center' }}>{result}</p>}

        <button
          className="btn lg block"
          disabled={!online || flushing || pending.length === 0}
          onClick={() => void flush()}
        >
          {flushing
            ? 'Sending…'
            : online
              ? `Send ${pending.length} item${pending.length === 1 ? '' : 's'}`
              : 'Send — available when online'}
        </button>

        <div className="mini-row">
          <StatMini label="Send automatically" value={online ? 'On' : 'Waiting'} note="resumes on 3G, 4G or Wi-Fi" />
          <StatMini
            label="Oldest item"
            value={pending.length > 0 ? ago(pending[0]!.clientAt) : '—'}
          />
        </div>
      </div>
    </>
  )
}
